import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiInfo } from 'react-icons/fi';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import visitService from '../../services/visitService';
import { useProject } from '../../context/ProjectContext';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { Modal } from '../Common/Modal';
import './PMDashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const PATIENT_BREAKDOWN_LABELS = ['Screened', 'Enrolled', 'Screen Failed', 'On Treatment', 'Completed'];
const PATIENT_BREAKDOWN_DATA = [14, 5, 2, 3, 0];
const PATIENT_BREAKDOWN_COLORS = ['#4f46e5', '#6366f1', '#f472b6', '#06b6d4', '#10b981'];

const PatientBreakdownChart = ({ chartType }) => {
  const data = {
    labels: PATIENT_BREAKDOWN_LABELS,
    datasets: [{
      label: 'Patients',
      data: PATIENT_BREAKDOWN_DATA,
      backgroundColor: PATIENT_BREAKDOWN_COLORS,
      borderColor: PATIENT_BREAKDOWN_COLORS.map(c => c + 'aa'),
      borderWidth: 1,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: chartType === 'pie' ? 'right' : 'top' } },
    scales: chartType === 'pie' ? {} : { y: { beginAtZero: true } },
  };
  return chartType === 'pie' ? <Pie data={data} options={options} /> : <Bar data={data} options={options} />;
};

const VISIT_TYPE_LABELS = {
  SSV: 'Site Selection Visit',
  SIV: 'Site Initiation Visit',
  SMV: 'Site Monitoring Visit',
  SCV: 'Site Close-Out Visit',
};
const formatVisitType = (code) => VISIT_TYPE_LABELS[code] || code || '—';

/** Site label: name with code in parentheses when both exist (e.g. Acme Hospital (SITE-01)). */
const formatSiteWithCode = (siteName, siteCode) => {
  const name = siteName != null && String(siteName).trim() ? String(siteName).trim() : '';
  const code = siteCode != null && String(siteCode).trim() ? String(siteCode).trim() : '';
  if (name && code) return `${name} (${code})`;
  return name || code || '—';
};

const visitIdOf = (v) => {
  const id = v?.scheduledVisitID ?? v?.ScheduledVisitID;
  return id != null && id !== '' ? id : '—';
};

/** Compact list of visit IDs for aggregated PM Dashboard rows. */
const formatVisitIdsCell = (ids) => {
  if (!ids || !ids.length) return '—';
  const sorted = [...ids].map((x) => Number(x)).filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);
  if (!sorted.length) return '—';
  if (sorted.length <= 10) return sorted.join(', ');
  return `${sorted.slice(0, 10).join(', ')} (+${sorted.length - 10} more)`;
};

/** Format date as DD-MMM-YYYY (e.g. 21-Feb-2026) for display across all PM Dashboard tabs */
const formatDateDDMMMYYYY = (date) => {
  if (date == null) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const PMDashboard = () => {
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all'
    ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode)
    : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    project: 'all',
    site: 'all',
    cra: 'all',
  });
  const [visits, setVisits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [cras, setCras] = useState([]);
  const [visitsModalOpen, setVisitsModalOpen] = useState(false);
  const [visitsModalTitle, setVisitsModalTitle] = useState('');
  const [visitsModalVisits, setVisitsModalVisits] = useState([]);
  const [patientBreakdownOpen, setPatientBreakdownOpen] = useState(false);
  const [patientBreakdownTitle, setPatientBreakdownTitle] = useState('');
  const [patientChartType, setPatientChartType] = useState('bar');
  const [detailSearch, setDetailSearch] = useState('');
  const [detailSortKey, setDetailSortKey] = useState('');
  const [detailSortDir, setDetailSortDir] = useState('asc');
  const [detailPage, setDetailPage] = useState(0);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const [overviewSearch, setOverviewSearch] = useState('');
  const [overviewPage, setOverviewPage] = useState(0);
  const [overviewPageSize, setOverviewPageSize] = useState(10);
  const [summarySearch, setSummarySearch] = useState('');
  const [summaryPage, setSummaryPage] = useState(0);
  const [summaryPageSize, setSummaryPageSize] = useState(10);
  const [patientsSearch, setPatientsSearch] = useState('');
  const [patientsPage, setPatientsPage] = useState(0);
  const [patientsPageSize, setPatientsPageSize] = useState(10);
  const PAGE_SIZE_OPTIONS = [10, 25, 50];

  useEffect(() => {
    fetchData();
  }, [filters, projectCode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [visitsData] = await Promise.all([
        visitService.getAll(projectCode || undefined),
      ]);
      // Exclude cancelled visits so all stats and tabs use active visits only
      const activeVisits = (visitsData || []).filter(v => v.visitStatus !== 'Cancelled');
      setVisits(activeVisits);

      // Extract unique values for filters (from active visits only). Site dropdown shows vSiteCode + vSiteDescription.
      const uniqueProjects = [...new Set(activeVisits.map(v => v.projectCode).filter(Boolean))];
      const siteMap = new Map();
      activeVisits.forEach(v => {
        const name = v.siteName;
        if (!name && !v.siteCode) return;
        const key = name || v.siteCode;
        if (siteMap.has(key)) return;
        const label = formatSiteWithCode(name, v.siteCode);
        siteMap.set(key, { value: key, label });
      });
      const uniqueSites = Array.from(siteMap.values());
      const uniqueCras = [...new Set(activeVisits.map(v => v.craName).filter(Boolean))];
      
      setProjects(uniqueProjects);
      setSites(uniqueSites);
      setCras(uniqueCras);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('PM Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Filter visits based on current filters
  const getFilteredVisits = () => {
    return visits.filter(v => {
      if (filters.project !== 'all' && v.projectCode !== filters.project) return false;
      if (filters.site !== 'all' && v.siteName !== filters.site && v.siteCode !== filters.site) return false;
      if (filters.cra !== 'all' && v.craName !== filters.cra) return false;
      return true;
    });
  };

  // Calculate statistics for Overview tab
  // Completed = visits with both ActualStartDate and ActualEndDate
  // Total Scheduled Visits = visits with both PlannedStartDate and PlannedEndDate, excluding completed (completed visits are not counted)
  const getStatistics = () => {
    const filtered = getFilteredVisits();

    const completed = filtered.filter(v =>
      v.actualStartDate && v.actualEndDate
    ).length;

    const scheduled = filtered.filter(v => {
      if (!v.plannedStartDate || !v.plannedEndDate) return false;
      if (v.actualStartDate && v.actualEndDate) return false; // completed visits excluded from Total Scheduled
      return true;
    }).length;

    return { scheduled, completed, pending: scheduled };
  };

  // Get CRA performance data (Completed = both ActualStartDate & ActualEndDate; scheduled = PlannedStartDate & PlannedEndDate, not completed)
  const getCRAPerformance = () => {
    const filtered = getFilteredVisits();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysLater = new Date(today);
    thirtyDaysLater.setDate(today.getDate() + 30);

    const craData = {};

    filtered.forEach(v => {
      const cra = v.craName;
      if (!cra) return;

      if (!craData[cra]) {
        craData[cra] = { pending: 0, upcoming: 0, completed: 0, total: 0 };
      }

      craData[cra].total++;

      const isCompleted = v.actualStartDate && v.actualEndDate;
      const hasScheduled = v.plannedStartDate && v.plannedEndDate;

      if (isCompleted) {
        craData[cra].completed++;
      } else if (hasScheduled) {
        craData[cra].pending++;
        const plannedDate = new Date(v.plannedStartDate);
        if (plannedDate > today && plannedDate <= thirtyDaysLater) {
          craData[cra].upcoming++;
        }
      }
    });

    return Object.entries(craData)
      .map(([cra, stats]) => ({ cra, ...stats }))
      .filter(c => c.total > 0)
      .sort((a, b) => a.pending - b.pending || a.upcoming - b.upcoming || a.completed - b.completed);
  };

  // Get schedule summary grouped by Project/Site/CRA (normalized key so same combo always groups together)
  const getScheduleSummary = () => {
    const filtered = getFilteredVisits();
    const groups = {};

    const norm = (s) => (s != null && typeof s === 'string' ? s.trim() : '') || '';

    filtered.forEach(v => {
      const project = norm(v.projectCode);
      const site = norm(v.siteName);
      const siteCode = norm(v.siteCode);
      const cra = norm(v.craName);
      const key = `${project}|${siteCode || site}|${site}|${cra}`;
      if (!groups[key]) {
        groups[key] = {
          project: (v.projectCode != null && String(v.projectCode).trim()) ? String(v.projectCode).trim() : '—',
          site: formatSiteWithCode(v.siteName, v.siteCode),
          cra: (v.craName != null && String(v.craName).trim()) ? String(v.craName).trim() : '—',
          coMonitor: (v.coMonitorName != null && String(v.coMonitorName).trim()) ? String(v.coMonitorName).trim() : '—',
          visits: [],
          latestVisit: null,
          latestPending: null,
        };
      }
      groups[key].visits.push(v);

      if (v.plannedStartDate && v.plannedEndDate) {
        const plannedDate = new Date(v.plannedStartDate);
        if (!groups[key].latestVisit || plannedDate > groups[key].latestVisit) {
          groups[key].latestVisit = plannedDate;
        }
        const isCompleted = v.actualStartDate && v.actualEndDate;
        if (!isCompleted && (!groups[key].latestPending || plannedDate > groups[key].latestPending)) {
          groups[key].latestPending = plannedDate;
        }
      }
    });

    return Object.values(groups)
      .sort((a, b) => (b.latestVisit ? b.latestVisit.getTime() : 0) - (a.latestVisit ? a.latestVisit.getTime() : 0));
  };

  const openVisitsModal = (title, visitList) => {
    setVisitsModalTitle(title);
    setVisitsModalVisits(visitList || []);
    setVisitsModalOpen(true);
  };

  const getScheduledVisitsForCRA = (craName) => {
    return getFilteredVisits().filter(v => {
      if (v.craName !== craName) return false;
      if (v.actualStartDate && v.actualEndDate) return false; // completed excluded
      return v.plannedStartDate && v.plannedEndDate;
    });
  };

  const getUpcomingVisitsForCRA = (craName) => {
    const filtered = getFilteredVisits();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysLater = new Date(today);
    thirtyDaysLater.setDate(today.getDate() + 30);
    return filtered.filter(v => {
      if (v.craName !== craName) return false;
      if (v.actualStartDate && v.actualEndDate) return false; // completed excluded
      if (!v.plannedStartDate || !v.plannedEndDate) return false;
      const d = new Date(v.plannedStartDate);
      return d > today && d <= thirtyDaysLater;
    });
  };

  const getCompletedVisitsForCRA = (craName) => {
    return getFilteredVisits().filter(v =>
      v.craName === craName && v.actualStartDate && v.actualEndDate
    );
  };

  const getPatientsOverview = () => {
    const filtered = getFilteredVisits();
    const grouped = {};
    const normPs = (s) => (s != null && typeof s === 'string' ? s.trim() : '') || '';
    filtered.forEach(v => {
      const key = `${v.projectCode}|${normPs(v.siteCode)}|${normPs(v.siteName)}`;
      if (!grouped[key]) {
        grouped[key] = {
          project: v.projectCode,
          site: formatSiteWithCode(v.siteName, v.siteCode),
          actual: 0,
          projected: 0,
          visitIds: [],
        };
      }
      grouped[key].actual += (v.actualPatients || 0);
      grouped[key].projected += (v.projectedPatients || 0);
      const id = visitIdOf(v);
      if (id !== '—') {
        const n = Number(id);
        if (!Number.isNaN(n) && !grouped[key].visitIds.includes(n)) grouped[key].visitIds.push(n);
      }
    });
    return Object.values(grouped);
  };

  const filteredDetailVisits = useMemo(() => {
    const list = getFilteredVisits();
    let result = list;
    if (detailSearch.trim()) {
      const q = detailSearch.trim().toLowerCase();
      result = result.filter(v =>
        (v.projectCode || '').toLowerCase().includes(q) ||
        (v.siteName || '').toLowerCase().includes(q) ||
        (v.SiteName || '').toLowerCase().includes(q) ||
        (v.siteCode || '').toLowerCase().includes(q) ||
        (v.SiteCode || '').toLowerCase().includes(q) ||
        (v.vSiteCode || '').toLowerCase().includes(q) ||
        String(visitIdOf(v)).toLowerCase().includes(q) ||
        (v.visitType || '').toLowerCase().includes(q) ||
        (v.craName || '').toLowerCase().includes(q) ||
        (v.coMonitorName || '').toLowerCase().includes(q)
      );
    }
    if (detailSortKey) {
      result = [...result].sort((a, b) => {
        let va = a[detailSortKey];
        let vb = b[detailSortKey];
        if (detailSortKey === 'scheduledVisitID') {
          va = Number(va) || 0;
          vb = Number(vb) || 0;
        } else if (detailSortKey.includes('Date')) {
          va = va ? new Date(va).getTime() : 0;
          vb = vb ? new Date(vb).getTime() : 0;
        }
        if (va < vb) return detailSortDir === 'asc' ? -1 : 1;
        if (va > vb) return detailSortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [visits, filters, detailSearch, detailSortKey, detailSortDir]);

  const detailTotalPages = Math.max(1, Math.ceil(filteredDetailVisits.length / detailPageSize));
  const detailPaginatedVisits = useMemo(() => {
    const start = detailPage * detailPageSize;
    return filteredDetailVisits.slice(start, start + detailPageSize);
  }, [filteredDetailVisits, detailPage, detailPageSize]);

  const handleDetailSort = (key) => {
    if (detailSortKey === key) {
      setDetailSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setDetailSortKey(key);
      setDetailSortDir('asc');
    }
    setDetailPage(0);
  };

  if (loading) return <Spinner />;

  const stats = getStatistics();
  const craPerformance = getCRAPerformance();
  const scheduleSummary = getScheduleSummary();
  const patientsOverview = getPatientsOverview();

  // Overview tab: filter CRA table by search, then paginate
  const overviewSearchLower = (overviewSearch || '').trim().toLowerCase();
  const filteredOverviewCRA = overviewSearchLower
    ? craPerformance.filter(c => (c.cra || '').toLowerCase().includes(overviewSearchLower))
    : craPerformance;
  const overviewTotalPages = Math.max(1, Math.ceil(filteredOverviewCRA.length / overviewPageSize));
  const overviewPaginatedCRA = filteredOverviewCRA.slice(overviewPage * overviewPageSize, (overviewPage + 1) * overviewPageSize);

  // Schedule Summary tab: filter cards by search, then paginate
  const summarySearchLower = (summarySearch || '').trim().toLowerCase();
  const filteredSummary = summarySearchLower
    ? scheduleSummary.filter(g =>
        (g.project || '').toLowerCase().includes(summarySearchLower) ||
        (g.site || '').toLowerCase().includes(summarySearchLower) ||
        (g.cra || '').toLowerCase().includes(summarySearchLower) ||
        (g.coMonitor || '').toLowerCase().includes(summarySearchLower)
      )
    : scheduleSummary;
  const summaryTotalPages = Math.max(1, Math.ceil(filteredSummary.length / summaryPageSize));
  const summaryPaginated = filteredSummary.slice(summaryPage * summaryPageSize, (summaryPage + 1) * summaryPageSize);

  // Patients Overview tab: filter by search, then paginate
  const patientsSearchLower = (patientsSearch || '').trim().toLowerCase();
  const filteredPatientsOverview = patientsSearchLower
    ? patientsOverview.filter(p =>
        (p.project || '').toLowerCase().includes(patientsSearchLower) ||
        (p.site || '').toLowerCase().includes(patientsSearchLower) ||
        (p.visitIds || []).some((id) => String(id).includes(patientsSearchLower))
      )
    : patientsOverview;
  const patientsTotalPages = Math.max(1, Math.ceil(filteredPatientsOverview.length / patientsPageSize));
  const patientsPaginated = filteredPatientsOverview.slice(patientsPage * patientsPageSize, (patientsPage + 1) * patientsPageSize);

  const handleDetailPageSizeChange = (e) => {
    setDetailPageSize(Number(e.target.value));
    setDetailPage(0);
  };
  const handleOverviewPageSizeChange = (e) => {
    setOverviewPageSize(Number(e.target.value));
    setOverviewPage(0);
  };
  const handleSummaryPageSizeChange = (e) => {
    setSummaryPageSize(Number(e.target.value));
    setSummaryPage(0);
  };
  const handlePatientsPageSizeChange = (e) => {
    setPatientsPageSize(Number(e.target.value));
    setPatientsPage(0);
  };

  return (
    <div className="pm-dashboard">
      <header className="pm-header">
        <div className="header-content">
          <h1>PM Dashboard</h1>
          <Link to="/dashboard" className="back-button">
            <FiArrowLeft /> Back to Main
          </Link>
        </div>
      </header>

      {error && <Alert type="error" message={error} />}

      {/* Filters */}
      <div className="pm-filters">
        <div className="filter-group">
          <label className="form-label">Project</label>
          <select
            className="form-select"
            value={filters.project}
            onChange={(e) => handleFilterChange('project', e.target.value)}
          >
            <option value="all">All</option>
            {projects.map(project => (
              <option key={project} value={project}>{project}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">Site</label>
          <select
            className="form-select"
            value={filters.site}
            onChange={(e) => handleFilterChange('site', e.target.value)}
          >
            <option value="all">All</option>
            {sites.map(site => (
              <option key={site.value} value={site.value}>{site.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label">CRA</label>
          <select
            className="form-select"
            value={filters.cra}
            onChange={(e) => handleFilterChange('cra', e.target.value)}
          >
            <option value="all">All</option>
            {cras.map(cra => (
              <option key={cra} value={cra}>{cra}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            style={{ width: '100%', padding: '0 !important' }}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'summary' ? 'active' : ''}`}
            style={{ width: '100%', padding: '0 !important' }}
            onClick={() => setActiveTab('summary')}
          >
            Schedule Summary
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'detailed' ? 'active' : ''}`}
            style={{ width: '100%', padding: '0 !important' }}
            onClick={() => setActiveTab('detailed')}
          >
            Detailed Data
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'patients' ? 'active' : ''}`}
            style={{ width: '100%', padding: '0 !important' }}
            onClick={() => setActiveTab('patients')}
          >
            Patients Overview
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="tab-content-area">
        {/* Overview Tab: two cards + CRAs Performance Summary table */}
        {activeTab === 'overview' && (
          <div className="tab-pane active">
            {getFilteredVisits().length === 0 ? (
              <div className="no-data-message">
                <FiInfo size={48} />
                <p>No visits match the selected filters.</p>
              </div>
            ) : (
              <>
                <div className="overview-cards overview-cards-two">
                  <div className="stat-card">
                    <h6 className="text-muted">Total Scheduled Visits</h6>
                    <div className="big-number text-primary">{stats.pending}</div>
                  </div>
                  <div className="stat-card">
                    <h6 className="text-muted">Completed</h6>
                    <div className="big-number text-success">{stats.completed}</div>
                  </div>
                </div>
                <div className="card pm-overview-cra-section">
                  <div className="card-header">
                    <h5 className="mb-0">CRAs Performance Summary</h5>
                  </div>
                  <div className="card-body">
                    {craPerformance.length === 0 ? (
                      <div className="no-data-message">
                        <FiInfo size={48} />
                        <p>No CRA data found under current filters.</p>
                      </div>
                    ) : (
                      <>
                        <div className="pm-tab-toolbar">
                          <input
                            type="search"
                            className="form-control pm-detail-search"
                            placeholder="Search"
                            value={overviewSearch}
                            onChange={(e) => { setOverviewSearch(e.target.value); setOverviewPage(0); }}
                          />
                          <div className="pm-items-per-page">
                            <select
                              className="form-select form-select-sm"
                              value={overviewPageSize}
                              onChange={handleOverviewPageSizeChange}
                              aria-label="Items per page"
                            >
                              {PAGE_SIZE_OPTIONS.map(n => (
                                <option key={n} value={n}>{n} items</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>CRA Name</th>
                              <th>Scheduled Visits</th>
                              <th>Completed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overviewPaginatedCRA.map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <strong>{item.cra}</strong>
                                  <br />
                                  <small className="text-muted">Total: {item.total}</small>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm rounded-pill px-3 fw-bold"
                                    onClick={() => openVisitsModal(`Scheduled Visits – ${item.cra}`, getScheduledVisitsForCRA(item.cra))}
                                  >
                                    {item.pending} Scheduled Visits
                                  </button>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-success btn-sm rounded-pill px-3 fw-bold"
                                    onClick={() => openVisitsModal(`Completed Visits – ${item.cra}`, getCompletedVisitsForCRA(item.cra))}
                                  >
                                    {item.completed} Completed
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                        <div className="pm-tab-pagination">
                          <span className="pm-tab-pagination-info">
                            Showing {filteredOverviewCRA.length === 0 ? 0 : overviewPage * overviewPageSize + 1} to {Math.min((overviewPage + 1) * overviewPageSize, filteredOverviewCRA.length)} of {filteredOverviewCRA.length} entries
                          </span>
                          <div className="pm-tab-pagination-btns">
                            <span className="pm-tab-pagination-page">Page {overviewPage + 1} of {overviewTotalPages}</span>
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={overviewPage <= 0} onClick={() => setOverviewPage(p => p - 1)}>Previous</button>
                            {Array.from({ length: overviewTotalPages }, (_, i) => (
                              <button key={i} type="button" className={`btn btn-sm ${overviewPage === i ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setOverviewPage(i)}>{i + 1}</button>
                            ))}
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={overviewPage >= overviewTotalPages - 1} onClick={() => setOverviewPage(p => p + 1)}>Next</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Schedule Summary Tab */}
        {activeTab === 'summary' && (
          <div className="tab-pane active">
            <div className="card">
              <div className="card-header">
                <h5>Project / Site / CRA wise Schedule Summary</h5>
              </div>
              <div className="card-body">
                {scheduleSummary.length === 0 ? (
                  <div className="no-data-message">
                    <FiInfo size={48} />
                    <p>No matching data found.</p>
                  </div>
                ) : (
                  <>
                    <div className="pm-tab-toolbar">
                      <input
                        type="search"
                        className="form-control pm-detail-search"
                        placeholder="Search"
                        value={summarySearch}
                        onChange={(e) => { setSummarySearch(e.target.value); setSummaryPage(0); }}
                      />
                      <div className="pm-items-per-page">
                        <select
                          className="form-select form-select-sm"
                          value={summaryPageSize}
                          onChange={handleSummaryPageSizeChange}
                          aria-label="Items per page"
                        >
                          {PAGE_SIZE_OPTIONS.map(n => (
                            <option key={n} value={n}>{n} items</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="schedule-grid">
                    {summaryPaginated.map((group, idx) => (
                      <div key={idx} className="summary-card">
                        <h6>{group.project}</h6>
                        <div className="mb-2"><strong>Site:</strong> {group.site}</div>
                        <div className="mb-2"><strong>CRA:</strong> {group.cra}</div>
                        <div className="mb-3"><strong>Co-Monitor:</strong> {group.coMonitor}</div>
                        <hr />
                        <div className="mb-3 d-flex justify-content-between align-items-center">
                          <span className="fw-medium">Assigned Visits</span>
                          <button
                            type="button"
                            className="badge bg-primary px-3 py-2 pm-badge-btn"
                            onClick={() => openVisitsModal(`Visits assigned to ${group.cra} (${group.project} – ${group.site})`, group.visits)}
                          >
                            {group.visits.length}
                          </button>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-medium">Latest Scheduled</span>
                          <span className="badge bg-outline-primary py-2">
                            {formatDateDDMMMYYYY(group.latestVisit)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-medium">Last Pending Visit Date</span>
                          <span className="badge bg-outline-warning py-2">
                            {formatDateDDMMMYYYY(group.latestPending)}
                          </span>
                        </div>
                      </div>
                    ))}
                    </div>
                    <div className="pm-tab-pagination">
                      <span className="pm-tab-pagination-info">
                        Showing {filteredSummary.length === 0 ? 0 : summaryPage * summaryPageSize + 1} to {Math.min((summaryPage + 1) * summaryPageSize, filteredSummary.length)} of {filteredSummary.length} entries
                      </span>
                      <div className="pm-tab-pagination-btns">
                        <span className="pm-tab-pagination-page">Page {summaryPage + 1} of {summaryTotalPages}</span>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={summaryPage <= 0} onClick={() => setSummaryPage(p => p - 1)}>Previous</button>
                        {Array.from({ length: summaryTotalPages }, (_, i) => (
                          <button key={i} type="button" className={`btn btn-sm ${summaryPage === i ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setSummaryPage(i)}>{i + 1}</button>
                        ))}
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={summaryPage >= summaryTotalPages - 1} onClick={() => setSummaryPage(p => p + 1)}>Next</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Detailed Data Tab */}
        {activeTab === 'detailed' && (
          <div className="tab-pane active">
            <div className="card">
              <div className="card-header">
                <h5>Detailed Visit Data</h5>
              </div>
              <div className="card-body">
                <div className="pm-tab-toolbar">
                  <input
                    type="search"
                    className="form-control pm-detail-search"
                    placeholder="Search"
                    value={detailSearch}
                    onChange={(e) => { setDetailSearch(e.target.value); setDetailPage(0); }}
                  />
                  <div className="pm-items-per-page">
                    <select
                      className="form-select form-select-sm"
                      value={detailPageSize}
                      onChange={handleDetailPageSizeChange}
                      aria-label="Items per page"
                    >
                      {PAGE_SIZE_OPTIONS.map(n => (
                        <option key={n} value={n}>{n} items</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover pm-detail-table">
                    <thead>
                      <tr>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('projectCode')}>Project {detailSortKey === 'projectCode' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('scheduledVisitID')}>Visit ID {detailSortKey === 'scheduledVisitID' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('siteName')}>Site {detailSortKey === 'siteName' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('visitType')}>Visit Type{detailSortKey === 'visitType' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('craName')}>CRA {detailSortKey === 'craName' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th>Co-Monitor</th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('plannedStartDate')}>Schedule Start {detailSortKey === 'plannedStartDate' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('plannedEndDate')}>Schedule End {detailSortKey === 'plannedEndDate' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('actualStartDate')}>Actual Start {detailSortKey === 'actualStartDate' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                        <th><button type="button" className="pm-th-sort" onClick={() => handleDetailSort('actualEndDate')}>Actual End {detailSortKey === 'actualEndDate' && (detailSortDir === 'asc' ? '↑' : '↓')}</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailPaginatedVisits.map((visit) => (
                        <tr key={visit.scheduledVisitID}>
                          <td>{visit.projectCode}</td>
                          <td>{visitIdOf(visit)}</td>
                          <td>{formatSiteWithCode(visit.siteName, visit.siteCode)}</td>
                          <td>{formatVisitType(visit.visitType)}</td>
                          <td>{visit.craName}</td>
                          <td>{visit.coMonitorName || '—'}</td>
                          <td className="pm-nowrap">{formatDateDDMMMYYYY(visit.plannedStartDate)}</td>
                          <td className="pm-nowrap">{formatDateDDMMMYYYY(visit.plannedEndDate)}</td>
                          <td className="pm-nowrap">{formatDateDDMMMYYYY(visit.actualStartDate)}</td>
                          <td className="pm-nowrap">{formatDateDDMMMYYYY(visit.actualEndDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pm-tab-pagination">
                  <span className="pm-tab-pagination-info">
                    Showing {filteredDetailVisits.length === 0 ? 0 : detailPage * detailPageSize + 1} to {Math.min((detailPage + 1) * detailPageSize, filteredDetailVisits.length)} of {filteredDetailVisits.length} entries
                  </span>
                  <div className="pm-tab-pagination-btns">
                    <span className="pm-tab-pagination-page">Page {detailPage + 1} of {detailTotalPages}</span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={detailPage <= 0} onClick={() => setDetailPage(p => p - 1)}>Previous</button>
                    {Array.from({ length: detailTotalPages }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`btn btn-sm ${detailPage === i ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setDetailPage(i)}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={detailPage >= detailTotalPages - 1} onClick={() => setDetailPage(p => p + 1)}>Next</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Patients Overview Tab */}
        {activeTab === 'patients' && (
          <div className="tab-pane active">
            <div className="card">
              <div className="card-header">
                <h5>Patients Overview (by Project & Site)</h5>
              </div>
              <div className="card-body">
                {patientsOverview.length === 0 ? (
                  <div className="no-data-message">
                    <FiInfo size={48} />
                    <p>No data found under current filters.</p>
                  </div>
                ) : (
                  <>
                    <div className="pm-tab-toolbar">
                      <input
                        type="search"
                        className="form-control pm-detail-search"
                        placeholder="Search"
                        value={patientsSearch}
                        onChange={(e) => { setPatientsSearch(e.target.value); setPatientsPage(0); }}
                      />
                      <div className="pm-items-per-page">
                        <select
                          className="form-select form-select-sm"
                          value={patientsPageSize}
                          onChange={handlePatientsPageSizeChange}
                          aria-label="Items per page"
                        >
                          {PAGE_SIZE_OPTIONS.map(n => (
                            <option key={n} value={n}>{n} items</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Visit ID(s)</th>
                          <th>Site</th>
                          <th>Actual / Projected Patients</th>
                          <th>View Chart</th>
                        </tr>
                      </thead>
                      <tbody>
                        {patientsPaginated.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.project}</td>
                            <td className="pm-visit-ids-cell" title={item.visitIds?.length ? item.visitIds.join(', ') : ''}>{formatVisitIdsCell(item.visitIds)}</td>
                            <td>{item.site}</td>
                            <td>{item.actual} / {item.projected}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => { setPatientBreakdownTitle(`${item.project} – ${item.site}`); setPatientBreakdownOpen(true); }}
                              >
                                View Chart
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <div className="pm-tab-pagination">
                      <span className="pm-tab-pagination-info">
                        Showing {filteredPatientsOverview.length === 0 ? 0 : patientsPage * patientsPageSize + 1} to {Math.min((patientsPage + 1) * patientsPageSize, filteredPatientsOverview.length)} of {filteredPatientsOverview.length} entries
                      </span>
                      <div className="pm-tab-pagination-btns">
                        <span className="pm-tab-pagination-page">Page {patientsPage + 1} of {patientsTotalPages}</span>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={patientsPage <= 0} onClick={() => setPatientsPage(p => p - 1)}>Previous</button>
                        {Array.from({ length: patientsTotalPages }, (_, i) => (
                          <button key={i} type="button" className={`btn btn-sm ${patientsPage === i ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPatientsPage(i)}>{i + 1}</button>
                        ))}
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={patientsPage >= patientsTotalPages - 1} onClick={() => setPatientsPage(p => p + 1)}>Next</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Assigned Visits Modal */}
      <Modal
        isOpen={visitsModalOpen}
        onClose={() => setVisitsModalOpen(false)}
        title={visitsModalTitle}
        size="large"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setVisitsModalOpen(false)}>Close</button>
        }
      >
        <div className="table-responsive">
          <table className="table table-hover table-bordered table-sm">
            <thead className="table-light">
              <tr>
                <th>Project</th>
                <th>Visit ID</th>
                <th>Site</th>
                <th>Visit Type</th>
                <th>Schedule Start</th>
                <th>Schedule End</th>
                <th>Actual Start</th>
                <th>Actual End</th>
              </tr>
            </thead>
            <tbody>
              {visitsModalVisits.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-4">No matching visits found.</td></tr>
              ) : (
                visitsModalVisits.map((v) => (
                  <tr key={v.scheduledVisitID}>
                    <td>{v.projectCode}</td>
                    <td>{visitIdOf(v)}</td>
                    <td>{formatSiteWithCode(v.siteName, v.siteCode)}</td>
                    <td>{formatVisitType(v.visitType)}</td>
                    <td>{formatDateDDMMMYYYY(v.plannedStartDate)}</td>
                    <td>{formatDateDDMMMYYYY(v.plannedEndDate)}</td>
                    <td>{formatDateDDMMMYYYY(v.actualStartDate)}</td>
                    <td>{formatDateDDMMMYYYY(v.actualEndDate)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal>

      {/* Patient Breakdown Modal */}
      <Modal
        isOpen={patientBreakdownOpen}
        onClose={() => setPatientBreakdownOpen(false)}
        title={`Patient Status Breakdown – ${patientBreakdownTitle}`}
        size="large"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setPatientBreakdownOpen(false)}>Close</button>
        }
      >
        <div className="pm-patient-breakdown">
          <div className="total-box">
            <h5 className="fw-bold text-primary mb-1">Total Patients</h5>
            <div className="display-5 fw-bold text-dark" id="totalPatientsCount">{[14, 5, 2, 3, 0].reduce((a, b) => a + b, 0)}</div>
          </div>
          <div className="text-center mb-4">
            <div className="btn-group" role="group">
              <button type="button" className={`btn btn-outline-primary ${patientChartType === 'bar' ? 'active' : ''}`} onClick={() => setPatientChartType('bar')}>Bar Chart</button>
              <button type="button" className={`btn btn-outline-primary ${patientChartType === 'pie' ? 'active' : ''}`} onClick={() => setPatientChartType('pie')}>Pie Chart</button>
            </div>
          </div>
          <div className="chart-container">
            <PatientBreakdownChart chartType={patientChartType} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PMDashboard;
